import {DemoTour} from './demo-tour.js';

// This class handles positioning the Slide Inner modal 
// and setting up the highlight data
export class DemoSlide {
  constructor(el){
    this.els = {};
    this.els.slide = el.classList.contains('demo-slide') ? el : el.querySelector('.demo-slide');
    if(!this.els.slide) return;

    this.isAdmin = document.body.classList.contains('wp-admin');
    if(this.isAdmin){
      this.els.slide.classList.add('active');
      // Highlight Selection State
      this.isHighlightSelectionActive = false;
      this.isSelectingHighlight = false;
      this.highlightEditorSelectionData = {x:0, y:0, width:0, height:0};
    }

    this.setBlockEls(); // Populates this.els with block elements
    this.getBlockHighlightData(); // Populates this.highlightData with existing highlight data

    this.shiftY = 0;
    this.shiftX = 0;

    this.init();
  }

  init(){
    // Admin specific init
    if(this.isAdmin){
      this.handleHighlightButtonClick();
    }

    // Watch resize and update block data
    let debouncer;
    if(this.isAdmin){
      window.addEventListener('resize', () => {
        clearTimeout(debouncer);
        debouncer = setTimeout(() => {
          this.setUI();
        }, 50);
      });
    }

    // Guards for placement reentry/queue
    this._placing = false;
    this._placeQueued = false;
    this._reflowRetries = 0;

    // Initial modal placement
    this.setUI(true);
    this.setupObservers();
  }

  setBlockEls = () => {
    this.els.highlightEl = this.els.slide.querySelector('.demo-slide__highlight');
    // Parent background container and inner fit element
    this.els.backgroundParent = this.els.slide.querySelector('.demo-slide__background');
    this.els.backgroundEl = this.els.slide.querySelector('.demo-slide__background_inner');
    this.els.modalEl = this.els.slide.querySelector('.demo-slide__modal');
    this.els.contentWrapper = this.els.slide.closest('.demo-slide__content-wrapper');
    
    // If in the admin, create a new element with the class of demo-slide__set-highlight
    if(this.isAdmin){
      this.els.highlightTrigger = document.createElement('button');
      this.els.highlightTrigger.type = 'button';
      this.els.highlightTrigger.className = 'demo-slide__set-highlight demo-tour__slide-set-highlight';
      this.els.highlightTrigger.innerText = 'Set Highlight Area';
      this.els.slide.querySelector('.demo-slide__background').appendChild(this.els.highlightTrigger);
    }
  }

  setBlockEditorFields = () => {
    if(!this.isAdmin) return;

    const acfGroup = document.querySelector('.acf-field-demo-bg-highlight');
    if(!acfGroup) return {};

    this.highlightFields = {};
    this.highlightFields.x = document.querySelector('.acf-field-demo-hx input');
    this.highlightFields.y = document.querySelector('.acf-field-demo-hy input');
    this.highlightFields.width = document.querySelector('.acf-field-demo-hw input');
    this.highlightFields.height = document.querySelector('.acf-field-demo-hh input');
  }

  getBlockClientID = () => {
    const blockClientId = this.els.slide.closest('.wp-block').getAttribute('data-block');
    return blockClientId;
  }

  getBlockHighlightData = () => {
    this.highlightData = {
      x: parseFloat(this.els.slide.dataset.highlightX) || 0,
      y: parseFloat(this.els.slide.dataset.highlightY) || 0,
      width: parseFloat(this.els.slide.dataset.highlightWidth) || 0,
      height: parseFloat(this.els.slide.dataset.highlightHeight) || 0,
    }
  }

  getDesktopModalSizeOverrides = () => {
    const normalizeSizeValue = (value) => {
      if(value == null) return null;

      const trimmedValue = String(value).trim();
      if(!trimmedValue) return null;

      return /^-?\d+(\.\d+)?$/.test(trimmedValue) ? `${trimmedValue}px` : trimmedValue;
    };

    return {
      width: normalizeSizeValue(this.els.slide.dataset.desktopModalWidth),
      height: normalizeSizeValue(this.els.slide.dataset.desktopModalHeight),
    };
  }

  setUI = (addLoadingClass) => {
    if(addLoadingClass) this.els.slide.classList.add('setting-ui');
    this.resetShifts();
    this._reflowRetries = 0;
    // Ensure modal scrollable content resets to top on UI set
    try {
      const scrollEl = this.els.slide.querySelector('.demo-tour__slide-modal-scrollable-content');
      const mobileScrollEl = this.els.slide.closest('.demo-tour__content-inner');
      if(scrollEl){
        scrollEl.scrollTop = 0;
      }
      if(mobileScrollEl){
        mobileScrollEl.scrollTop = 0;
      }
    } catch(e) { /* no-op */ }
    
    setTimeout(() => {
      this.setBackgroundImageRectData();
      this.ensureHighlightVisible();

      setTimeout(() => {
        // Double RAF to ensure layout settles (fonts/images)
        if(!this.isAdmin){
          requestAnimationFrame(()=>{
            requestAnimationFrame(()=>{
              this.queuePlaceModal();
            });
          });
        }

        setTimeout(() => {
          if(addLoadingClass) this.els.slide.classList.remove('setting-ui');
        }, 50);
      }, 50);
    }, 50);
  }

  setupObservers = () => {
    // Recompute placement when background resizes or media loads
    try {
      if(this.els.backgroundParent){
        // ResizeObserver on the outer background
        const ro = new ResizeObserver(() => {
          this.setBackgroundImageRectData();
          this.ensureHighlightVisible();
          this.queuePlaceModal();
        });
        ro.observe(this.els.backgroundParent);
        this._resizeObserver = ro;
      }

      // Listen for image/video load within background
      const media = this.els.backgroundParent ? this.els.backgroundParent.querySelector('.demo-slide__bg-media') : null;
      if(media){
        const trigger = () => {
          this.setBackgroundImageRectData();
          this.ensureHighlightVisible();
          this.queuePlaceModal();
        };
        if(media.tagName === 'IMG'){
          if(media.complete){ trigger(); }
          else { media.addEventListener('load', trigger, { once: true }); }
        } else if(media.tagName === 'VIDEO'){
          if(media.readyState >= 2){ trigger(); }
          else { media.addEventListener('loadeddata', trigger, { once: true }); }
        }
      }

      // Recompute after web fonts load
      if(document.fonts && document.fonts.ready){
        document.fonts.ready.then(()=>{
          this.queuePlaceModal();
        });
      }
    } catch(e) { /* no-op */ }
  }

  resumeAutoplayVideos = () => {
    const videos = this.els.slide ? this.els.slide.querySelectorAll('video[autoplay]') : [];
    if(!videos || videos.length === 0) return;

    videos.forEach((video) => {
      if(!video || !video.isConnected) return;

      const startVideo = () => {
        if(!video.paused && !video.ended) return;

        video.play().catch(() => {
          // Ignore autoplay re-entry failures caused by browser policy or transient load state.
        });
      };

      if(video.readyState >= 2){
        startVideo();
        return;
      }

      video.addEventListener('loadeddata', startVideo, { once: true });
      video.addEventListener('canplay', startVideo, { once: true });
    });
  }
  
  resetShifts = () => {
    this.shiftX = 0;
    this.shiftY = 0;
    if(this.els.backgroundEl){
      this.els.backgroundEl.style.setProperty('--bg-shift-x', `0px`);
      this.els.backgroundEl.style.setProperty('--bg-shift-y', `0px`);
    }
  }

  setBackgroundImageRectData = () => {
    if(!this.els.backgroundEl || !this.els.backgroundParent) return;
    const innerRect = this.els.backgroundEl.getBoundingClientRect();
    const outerRect = this.els.backgroundParent.getBoundingClientRect();
    const highlightRect = this.els.highlightEl ? this.els.highlightEl.getBoundingClientRect() : null;

    this.bgRect = {
      outer : outerRect,
      inner : innerRect,
      highlight : highlightRect
    }
  }

  ensureHighlightVisible = () => {
    if(!this.els.highlightEl || !this.bgRect) return;
    
    // If highlight data is empty, just return
    const {x, y, width, height} = this.highlightData || {};
    if(x == null || y == null || width == null || height == null) return;

    let highlightAvailableSpaceX;
    let highlightAvailableSpaceY;
    let innerOverlapX;
    let innerOverlapY;
    let shiftX;
    let shiftY;

    const EDGE_PAD = 20; // preferred gap from outer edge when space allows
    // Shift background right
    if(this.bgRect.highlight.left < this.bgRect.outer.left && this.bgRect.highlight.right < this.bgRect.outer.right){
      highlightAvailableSpaceX = this.bgRect.outer.right - this.bgRect.highlight.right; // Ensures we don't shift too far
      innerOverlapX = this.bgRect.outer.left - this.bgRect.inner.left; // Checks how much the inner el can move
      // Amount needed to bring highlight's left edge to outer left
      const neededShift = (this.bgRect.outer.left - this.bgRect.highlight.left) + EDGE_PAD; 
      shiftX = Math.min(neededShift, innerOverlapX, highlightAvailableSpaceX); // Only move so far as we can

      if(shiftX < 0) shiftX = 0; // Don't shift negatively

      this.shiftX = shiftX;
    }
    // Shift background left
    else if(this.bgRect.highlight.right > this.bgRect.outer.right && this.bgRect.highlight.left > this.bgRect.outer.left){
      // Move background left so highlight fits within the right boundary
      highlightAvailableSpaceX = this.bgRect.highlight.left - this.bgRect.outer.left; // free space on the left side
      innerOverlapX = this.bgRect.inner.right - this.bgRect.outer.right; // how much inner can move left
      const neededShift = (this.bgRect.highlight.right - this.bgRect.outer.right) + EDGE_PAD; // include pad from edge
      shiftX = Math.min(neededShift, innerOverlapX, highlightAvailableSpaceX);

      shiftX = shiftX * -1; // invert to negative for left shift

      if(shiftX > 0) shiftX = 0;  

      this.shiftX = shiftX;
    }

    // Shift background down
    if(this.bgRect.highlight.top < this.bgRect.outer.top && this.bgRect.highlight.bottom < this.bgRect.outer.bottom){
      // Move background down so highlight's top comes into view
      highlightAvailableSpaceY = this.bgRect.outer.bottom - this.bgRect.highlight.bottom;
      innerOverlapY = this.bgRect.outer.top - this.bgRect.inner.top; // how much inner can move down
      const neededShiftY = (this.bgRect.outer.top - this.bgRect.highlight.top) + EDGE_PAD;
      shiftY = Math.min(neededShiftY, innerOverlapY, highlightAvailableSpaceY);

      if(shiftY < 0) shiftY = 0;

      this.shiftY = shiftY;
    }
    // Shift background up
    else if(this.bgRect.highlight.bottom > this.bgRect.outer.bottom && this.bgRect.highlight.top > this.bgRect.outer.top){
      // Move background up so highlight's bottom comes into view
      highlightAvailableSpaceY = this.bgRect.highlight.top - this.bgRect.outer.top; // free space above
      innerOverlapY = this.bgRect.inner.bottom - this.bgRect.outer.bottom; // how much inner can move up
      const neededShiftY = (this.bgRect.highlight.bottom - this.bgRect.outer.bottom) + EDGE_PAD;
      shiftY = Math.min(neededShiftY, innerOverlapY, highlightAvailableSpaceY);

      shiftY = shiftY * -1; // invert to negative for up shift

      if(shiftY > 0) shiftY = 0;

      this.shiftY = shiftY;
    }

    // Set the CSS custom properties
    this.els.backgroundEl.style.setProperty('--bg-shift-x', `${this.shiftX}px`);
    this.els.backgroundEl.style.setProperty('--bg-shift-y', `${this.shiftY}px`);
  }

  // Determine optimal modal placement relative to highlight
  placeModal = () => {
    if(this._placing) return;
    this._placing = true;
    // console.log('Placing modal');
    if((!this.els.backgroundParent && !this.els.backgroundEl) || !this.els.modalEl){ this._placing=false; return; }
    if(!this.bgRect || !this.bgRect.outer){ this._placing=false; return; }

    // Use outer background bounds; inner may have moved to emulate object-fit
    const W = this.bgRect.outer.width;
    const H = this.bgRect.outer.height;

    // Measure highlight vs outer (in pixels)
    const highlightRect = this.els.highlightEl ? this.els.highlightEl.getBoundingClientRect() : null;
    if(!highlightRect){ this._placing=false; return; }
    const hLeft = Math.max(0, highlightRect.left - this.bgRect.outer.left);
    const hTop = Math.max(0, highlightRect.top - this.bgRect.outer.top);
    const hWidth = Math.min(W, highlightRect.width);
    const hHeight = Math.min(H, highlightRect.height);

    // Available spaces around highlight
    const spaceLeft = { x: 0, y: 0, width: Math.max(hLeft, 0), height: H };
    const spaceRight = { x: hLeft + hWidth, y: 0, width: Math.max(W - (hLeft + hWidth), 0), height: H };
    const spaceTop = { x: 0, y: 0, width: W, height: Math.max(hTop, 0) };
    const spaceBottom = { x: 0, y: hTop + hHeight, width: W, height: Math.max(H - (hTop + hHeight), 0) };

    const regions = [
      { name: 'left', ...spaceLeft },
      { name: 'right', ...spaceRight },
      { name: 'top', ...spaceTop },
      { name: 'bottom', ...spaceBottom },
    ];

    // Modal content characteristics
    const hasColumns = !!this.els.modalEl.querySelector('.wp-block-columns');

    // Score regions: primarily by area, with subtle bias
    const scored = regions.map(r => {
      const area = r.width * r.height;
      const aspect = r.width / (r.height || 1);

      // Penalize harsh aspect ratios to avoid overly thin/tall or wide/short areas
      const minAspect = 0.6;   // too tall/narrow
      const maxAspect = 1.8;   // too wide/short
      let aspectPenalty = 1;
      if (aspect < minAspect) {
        aspectPenalty = Math.max(0.5, aspect / minAspect);
      } else if (aspect > maxAspect) {
        aspectPenalty = Math.max(0.5, maxAspect / aspect);
      }

      // subtle bias: columns prefer width; otherwise prefer height
      const bias = hasColumns ? aspect : (1 / (aspect || 1));
      const biasWeight = 0.08; // keep subtle
      const score = area * aspectPenalty * (1 + biasWeight * (bias - 1));
      return { ...r, area, aspect, score };
    });

    // Choose best
    scored.sort((a,b)=> b.score - a.score);
    const best = scored[0];

    const modalSizeOverrides = this.getDesktopModalSizeOverrides();

    // Compute modal anchor point within chosen region: keep close to highlight with padding
    const modalRect = this.els.modalEl.getBoundingClientRect();
    let mW = modalRect.width;
    let mH = modalRect.height;

    if(modalSizeOverrides.width){
      this.els.modalEl.style.setProperty('--modal-width', modalSizeOverrides.width);
    } else {
      this.els.modalEl.style.removeProperty('--modal-width');
    }

    if(modalSizeOverrides.height){
      this.els.modalEl.style.setProperty('--modal-height', modalSizeOverrides.height);
    } else {
      this.els.modalEl.style.removeProperty('--modal-height');
    }

    // Fallback if not yet laid out
    if(!mW || !mH){
      mW = 390; mH = 300;
    }

    const PAD = 40; // preferred gap between modal and highlight
    let targetLeft = best.x;
    let targetTop = best.y;

    switch(best.name){
      case 'left':
        // Place modal to the left of highlight, aligned vertically near highlight center
        targetLeft = Math.max(best.x, hLeft - PAD - mW);
        targetTop = hTop + (hHeight/2) - (mH/2);
        break;
      case 'right':
        // Place modal to the right of highlight
        targetLeft = Math.min(best.x + best.width - mW, hLeft + hWidth + PAD);
        targetTop = hTop + (hHeight/2) - (mH/2);
        break;
      case 'top':
        // Place modal above highlight
        targetTop = Math.max(best.y, hTop - PAD - mH);
        targetLeft = hLeft + (hWidth/2) - (mW/2);
        break;
      case 'bottom':
        // Place modal below highlight
        targetTop = Math.min(best.y + best.height - mH, hTop + hHeight + PAD);
        targetLeft = hLeft + (hWidth/2) - (mW/2);
        break;
    }

    // Clamp inside background with preferred edge padding when space allows
    const EDGE_PAD = 20;
    const MIN_MODAL_MAX_HEIGHT = 300;
    const MIN_ARROW_Y = 30;
    const canPadX = (W - mW) >= (EDGE_PAD * 2);
    const canPadY = (H - mH) >= (EDGE_PAD * 2);
    const minClampX = canPadX ? EDGE_PAD : 0;
    const maxClampX = canPadX ? (W - mW - EDGE_PAD) : (W - mW);
    const minClampY = canPadY ? EDGE_PAD : 0;
    const maxClampY = canPadY ? (H - mH - EDGE_PAD) : (H - mH);
    targetLeft = Math.max(minClampX, Math.min(maxClampX, targetLeft));
    targetTop = Math.max(minClampY, Math.min(maxClampY, targetTop));

    // Save as CSS custom properties on modal for CSS to use
    this.els.modalEl.style.setProperty('--modal-left-pos', `${Math.round(targetLeft)}px`);
    this.els.modalEl.style.setProperty('--modal-top-pos', `${Math.round(targetTop)}px`);
    // Account for edge padding and highlight PAD in usable region size for max dimensions
    let usableWidth = best.width;
    let usableHeight = best.height;
    if (best.name === 'left' || best.name === 'right') {
      // leave vertical room for top/bottom padding
      usableHeight = Math.max(0, best.height - (canPadY ? EDGE_PAD * 2 : 0));
      // along horizontal axis, respect both outer edge pad (if any) and PAD gap to highlight
      const horizontalPads = (canPadX ? EDGE_PAD : 0) + PAD;
      usableWidth = Math.max(0, best.width - horizontalPads);
    } else {
      // top/bottom regions: leave horizontal room for left/right padding
      usableWidth = Math.max(0, best.width - (canPadX ? EDGE_PAD * 2 : 0));
      // along vertical axis, respect both outer edge pad (if any) and PAD gap to highlight
      const verticalPads = (canPadY ? EDGE_PAD : 0) + PAD;
      usableHeight = Math.max(0, best.height - verticalPads);
    }
    const modalMaxHeight = Math.max(MIN_MODAL_MAX_HEIGHT, usableHeight);
    if(modalSizeOverrides.width){
      this.els.modalEl.style.setProperty('--modal-max-width', '100%');
    } else {
      this.els.modalEl.style.setProperty('--modal-max-width', `${Math.round(usableWidth)}px`);
    }
    if(modalSizeOverrides.height){
      this.els.modalEl.style.setProperty('--modal-max-height', '100%');
    } else {
      this.els.modalEl.style.setProperty('--modal-max-height', `${Math.round(modalMaxHeight)}px`);
    }

    // Arrow position: point towards the highlight from the nearest side of modal
    // Compute highlight center relative to modal
    const highlightCenterX = hLeft + (hWidth / 2);
    const highlightCenterY = hTop + (hHeight / 2);
    const modalCenterX = targetLeft + (mW / 2);
    const modalCenterY = targetTop + (mH / 2);

    // Default arrow on the nearest edge, centered towards highlight
    let arrowX = mW / 2; // relative to modal (0..mW)
    let arrowY = mH / 2; // relative to modal (0..mH)

    switch(best.name){
      case 'left':
        // Arrow on right edge, vertical position towards highlight
        arrowX = mW; // right edge
        arrowY = Math.max(MIN_ARROW_Y, Math.min(mH, (highlightCenterY - targetTop)));
        break;
      case 'right':
        // Arrow on left edge
        arrowX = 0; // left edge
        arrowY = Math.max(MIN_ARROW_Y, Math.min(mH, (highlightCenterY - targetTop)));
        break;
      case 'top':
        // Arrow on bottom edge
        arrowX = Math.max(0, Math.min(mW, (highlightCenterX - targetLeft)));
        arrowY = mH; // bottom edge
        break;
      case 'bottom':
        // Arrow on top edge
        arrowX = Math.max(0, Math.min(mW, (highlightCenterX - targetLeft)));
        arrowY = 0; // top edge
        break;
      default:
        // Fallback: choose closest axis
        if(Math.abs(highlightCenterX - modalCenterX) > Math.abs(highlightCenterY - modalCenterY)){
          arrowX = (highlightCenterX < modalCenterX) ? 0 : mW;
          arrowY = Math.max(MIN_ARROW_Y, Math.min(mH, (highlightCenterY - targetTop)));
        } else {
          arrowY = (highlightCenterY < modalCenterY) ? 0 : mH;
          arrowX = Math.max(0, Math.min(mW, (highlightCenterX - targetLeft)));
        }
        break;
    }

    this.els.modalEl.style.setProperty('--arrow-x', `${Math.round(arrowX)}px`);
    this.els.modalEl.style.setProperty('--arrow-y', `${Math.round(arrowY)}px`);
    this.els.modalEl.dataset.arrowDir = best.name || '';

    // If the computed position changed significantly, schedule a reflow check
    requestAnimationFrame(()=>{
      const rectAfter = this.els.modalEl.getBoundingClientRect();
      // Re-evaluate short-height class after layout settles
      const isShortAfter = rectAfter.height < 500;
      this.els.modalEl.classList.toggle('is-short-height', isShortAfter);
      const changed = (Math.abs(rectAfter.height - mH) > 5 || Math.abs(rectAfter.width - mW) > 5);
      if(changed && this._reflowRetries < 2){
        this._reflowRetries += 1;
        this._placing = false;
        this.queuePlaceModal();
        return;
      }
      this._placing = false;
      this._reflowRetries = 0;
    });
  }

  // Batch placement requests to once-per-frame
  queuePlaceModal = () => {
    if(this._placeQueued) return;
    this._placeQueued = true;
    requestAnimationFrame(() => {
      this._placeQueued = false;
      this.placeModal();

      requestAnimationFrame(() => {
        // Toggle class based on modal height threshold
        if(!this.els.modalEl) return;
        const modalHeight = this.els.modalEl.getBoundingClientRect().height;
        if(Math.round(modalHeight) && Math.round(modalHeight) < 400){
          this.els.modalEl.classList.add('is-short-height');
        }
        else{
          this.els.modalEl.classList.remove('is-short-height');
        }
      });
    });
  }

  // Used in the editor via the highlight selection process
  setBlockHighlightData = () => {
    const {x, y, width, height} = this.highlightEditorSelectionData || {};
    
    if(x == null || y == null || width == null || height == null) return;
    
    // Once we're sure we have valid data, store it
    this.highlightData = this.highlightEditorSelectionData;

    // Update Style custom properties on the block element
    this.els.slide.style.setProperty('--highlight-left', `${x}%`);
    this.els.slide.style.setProperty('--highlight-top', `${y}%`);
    this.els.slide.style.setProperty('--highlight-width', `${width}%`);
    this.els.slide.style.setProperty('--highlight-height', `${height}%`);

    // Update Data Attributes on the block element
    this.els.slide.dataset.highlightX = x;
    this.els.slide.dataset.highlightY = y;
    this.els.slide.dataset.highlightWidth = width;
    this.els.slide.dataset.highlightHeight = height;

    // Update ACF fields if in admin
    if(this.isAdmin){
      this.updateAcfFields();
    }
  }

  handleHighlightButtonClick = () => {
    if(!this.isAdmin || !this.els.highlightTrigger || !this.els.backgroundEl) return;

    const handleClick = (e) => {
      e.preventDefault();
      
      // Hide trigger button
      this.els.highlightTrigger.style.display = 'none';
      this.els.backgroundEl.classList.add('highlight-selection-active');

      // Clear previous highlight selection data
      this.highlightEditorSelectionData = {x:0, y:0, width:0, height:0};
      // Remove any existing highlight css custom props
      this.els.slide.style.removeProperty('--highlight-left');
      this.els.slide.style.removeProperty('--highlight-top');
      this.els.slide.style.removeProperty('--highlight-width');
      this.els.slide.style.removeProperty('--highlight-height');

      // Remove data atts
      delete this.els.slide.dataset.highlightX;
      delete this.els.slide.dataset.highlightY;
      delete this.els.slide.dataset.highlightWidth;
      delete this.els.slide.dataset.highlightHeight;

      this.startHighlightSelection();
    };

    this.els.highlightTrigger.removeEventListener('click', handleClick);
    this.els.highlightTrigger.addEventListener('click', handleClick);
  }

  startHighlightSelection = () => {
    this.isHighlightSelectionActive = true;

    let startX, startY;
    const onMouseDown = (e) => {
      // ensure we're over the block background
      if(!e.target.closest('.demo-slide__background')) return;

      startX = e.clientX - this.els.backgroundEl.getBoundingClientRect().left;
      startY = e.clientY - this.els.backgroundEl.getBoundingClientRect().top;

      // Prevent the gutenberg block from being selected
      e.preventDefault();
      this.isSelectingHighlight = true;
      setData(e);
      this.setBlockHighlightData();
    };

    const onMouseMove = (e) => {
      if(!this.isSelectingHighlight || !this.isHighlightSelectionActive) return;
      
      setData(e);
      this.setBlockHighlightData();
    };

    const onMouseUp = (e) => {
      if(!this.isSelectingHighlight || !this.isHighlightSelectionActive) return;

      setData(e);
      
      // Ensure the highlight is killed until the trigger is reclicked
      this.els.backgroundEl.removeEventListener('mousedown', onMouseDown);
      this.els.backgroundEl.removeEventListener('mousemove', onMouseMove);
      
      this.isSelectingHighlight = false;
      this.isHighlightSelectionActive = false;

      // Show the highlight trigger button again
      this.els.highlightTrigger.style.display = 'block';
      this.els.backgroundEl.classList.remove('highlight-selection-active');
      
      this.setBlockHighlightData();
      this.setUI(true);
    };

    const setData = (e) => {
      const rect = this.els.backgroundEl.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      // Always compute top-left based on min of start and current
      const leftPx = Math.min(startX, currentX);
      const topPx = Math.min(startY, currentY);
      const widthPx = Math.abs(currentX - startX);
      const heightPx = Math.abs(currentY - startY);

      // Convert to percentages of background size, clamped to [0,100]
      const clamp = (v,min=0,max=100)=> Math.max(min, Math.min(max, v));
      let xPct = clamp((leftPx / rect.width) * 100);
      let yPct = clamp((topPx / rect.height) * 100);
      let wPct = clamp((widthPx / rect.width) * 100);
      let hPct = clamp((heightPx / rect.height) * 100);

      // Ensure selection stays within bounds: left+width <= 100, top+height <= 100
      if(xPct + wPct > 100) wPct = 100 - xPct;
      if(yPct + hPct > 100) hPct = 100 - yPct;

      this.highlightEditorSelectionData.x = Math.round(xPct);
      this.highlightEditorSelectionData.y = Math.round(yPct);
      this.highlightEditorSelectionData.width = Math.round(wPct);
      this.highlightEditorSelectionData.height = Math.round(hPct);
    }

    // Add new listeners
    this.els.backgroundEl.addEventListener('mousedown', onMouseDown);
    this.els.backgroundEl.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp, { once: true });
  }

  updateAcfFields = () => {
    if(!this.isSelectingHighlight || !this.isHighlightSelectionActive) return;
    if(typeof this.highlightData.x !== 'number' || typeof this.highlightData.y !== 'number' || typeof this.highlightData.width !== 'number' || typeof this.highlightData.height !== 'number') return;

    // Ensure values save to block attributes
    this.updateAcfFieldByKey('field_demo_hx', this.highlightData.x);
    this.updateAcfFieldByKey('field_demo_hy', this.highlightData.y);
    this.updateAcfFieldByKey('field_demo_hw', this.highlightData.width);
    this.updateAcfFieldByKey('field_demo_hh', this.highlightData.height);
  }

  updateAcfFieldByKey = (fieldKey, newValue) => {
    const field = acf.getField(fieldKey);
    if (!field) return;

    field.val(newValue); // this updates ACF's model AND triggers change
  };
}


export const initDemoSlideFrontEnd = () => {
  const slides = document.querySelectorAll('.demo-slide');
  if(!slides.length) return;

  slides.forEach(slide => {
    const demoSlideInstance = new DemoSlide(slide);
    demoSlideInstance.init();
  });
}

const updateEditorSlideCounts = () => {
  const slides = Array.from(document.querySelectorAll('.wp-block[data-type="acf/demo-slide"] .demo-slide'));
  const total = slides.length;
  if(!total) return;

  slides.forEach((slide, idx) => {
    const meta = slide.querySelector('.demo-slide__meta, .demo-tour__slide-meta');
    if(!meta) return;

    const existing = meta.querySelector('.demo-slide__count');
    if(existing) existing.remove();

    const countEl = document.createElement('span');
    countEl.className = 'demo-slide__count';
    countEl.textContent = `${idx + 1} / ${total}`;

    const eyebrow = meta.querySelector('.demo-slide__eyebrow');
    if(eyebrow && eyebrow.parentNode === meta){
      eyebrow.insertAdjacentElement('afterend', countEl);
      return;
    }

    meta.appendChild(countEl);
  });
}

let editorSlideCountsObserver = null;
let editorSlideCountsFrame = null;

const queueEditorSlideCountsUpdate = () => {
  if(editorSlideCountsFrame) return;

  editorSlideCountsFrame = requestAnimationFrame(() => {
    editorSlideCountsFrame = null;
    updateEditorSlideCounts();
  });
}

const initEditorSlideCountsObserver = () => {
  if(editorSlideCountsObserver) return;

  const editorRoot = document.querySelector('.block-editor-block-list__layout') || document.body;
  if(!editorRoot) return;

  editorSlideCountsObserver = new MutationObserver((mutations) => {
    const shouldUpdate = mutations.some((mutation) => {
      if(mutation.type !== 'childList') return false;

      const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
      return changedNodes.some((node) => {
        if(!(node instanceof Element)) return false;

        return node.matches('.wp-block[data-type="acf/demo-slide"], .demo-slide')
          || !!node.querySelector('.wp-block[data-type="acf/demo-slide"], .demo-slide');
      });
    });

    if(shouldUpdate){
      queueEditorSlideCountsUpdate();
    }
  });

  editorSlideCountsObserver.observe(editorRoot, { childList: true, subtree: true });
}

export const initDemoSlideEditor = () => {
  // Hook into ACF preview lifecycle so newly rendered blocks get wired up
  if(window.acf && window.acf.addAction){
    initEditorSlideCountsObserver();

    window.acf.addAction('render_block_preview/type=demo-slide', function($el){
      const block = !!$el[0] ? $el[0] : $el;
      if(!block) return;

      if(block.classList.contains('editor-initialized')){ 
        // block.instance.setBlockEls();
        // block.instance.setBlockEditorFields();
        // block.instance.getBlockHighlightData();
        // block.instance.setUI(true);
        // block.instance.setupObservers();
        queueEditorSlideCountsUpdate();
        return;
      }
      else {
        block.classList.add('editor-initialized');
        block.instance = new DemoSlide(block);
        queueEditorSlideCountsUpdate();
      }
    });
  }
}